import {
  DatePermission,
  DCEventTypes,
  EventTypes,
  IDCAnimalDataEvent,
  IDCCoverCroppingEvent,
  IDCFertilizerEvent,
  IDCForageEvent,
  IDCHarvestEvent,
  IDCPlantingEvent,
  IDCTillageEvent,
  IEvents,
  PermissionDetails,
  Permissions,
} from '../interfaces';
import { v4 as uuidv4 } from 'uuid';
import { Box, Text } from 'grommet';
import { Field } from '../../../../types/Field';
import { SF_PRACTICES_MAP as practiceNames } from '../../../../../map/service/common/constants';
import { SF_PRACTICES_MAP as practiceCodes } from '../../../../../../redux/actions/map-actions/field/field-helper';

export function orderedDCEvents(events: any, type: EventTypes) {
  let field = '';

  switch (type) {
    case 'Tillage':
      field = 'tillageDate';
      break;

    case 'Planting':
      field = 'plantingDate';
      break;

    case 'Harvest':
      field = 'harvestDate';
      break;

    case 'CoverCrop':
      field = 'plantingDate';
      break;

    case 'Fertilization':
      field = 'dateApplied';
      break;

    case 'Forage':
      field = 'dateOfApplication';
      break;

    case 'FieldData':
      field = 'year';
      break;

    case 'AnimalData':
      field = 'year';
      break;
  }

  return [...events]?.sort((a, b) => {
    if (a.newEvent && !b.newEvent) return -1;
    if (!a.newEvent && b.newEvent) return 1;

    if (a.newEvent && b.newEvent) {
      return new Date(b.newEvent.createdAt).getTime() - new Date(a.newEvent.createdAt).getTime();
    }

    const aHasField = !!a[field];
    const bHasField = !!b[field];

    if (!aHasField && bHasField) return -1;
    if (aHasField && !bHasField) return 1;

    if (aHasField && bHasField) {
      const dateA = new Date(getDateText(a[field])).getTime();
      const dateB = new Date(getDateText(b[field])).getTime();
      return dateB - dateA;
    }

    return 0;
  });
}

export function createNewEvent(fieldId: string | null, eventType: EventTypes, additionalInfo?: any): any {
  const defaultEvents = {
    Tillage: {
      id: uuidv4(),
      tillageDate: '',
      tillageTypeId: '',
      fieldId,
      newEvent: true,
      createdAt: new Date(),
    },
    Planting: {
      id: uuidv4(),
      plantingDate: '',
      plantSpeciesId: '',
      fieldId,
      newEvent: true,
      createdAt: new Date(),
    },
    Harvest: {
      id: uuidv4(),
      plantingId: additionalInfo?.plantingId,
      harvestDate: '',
      residueRemoval: additionalInfo?.residueRemoval,
      harvestMethodId: additionalInfo?.harvestMethodId,
      yield: '',
      // yieldUsedOnFarm: '',
      yieldUnitId: additionalInfo?.yieldUnitId,
      fieldId,
      newEvent: true,
      createdAt: new Date(),
    },
    CoverCrop: {
      id: uuidv4(),
      terminationMethodId: '',
      plantingDate: '',
      terminationDate: '',
      interseeding: false,
      species: [],
      modelInputId: '',
      fieldId,
      newEvent: true,
      createdAt: new Date(),
    },
    Fertilization: {
      id: uuidv4(),
      fertilizerTypeId: additionalInfo?.fertilizerTypeId ?? null,
      dateApplied: '',
      amount: '',
      amountUnitId: '',
      organicSourceId: additionalInfo?.organicSourceId ?? null,
      fieldId: fieldId,
      newEvent: true,
      createdAt: new Date(),
      appliedAcreage: additionalInfo?.initialAppliedAcreage,
    },
    Forage: {
      id: uuidv4(),
      dateOfApplication: '',
      plantSpecies: [],
      speciesModelInputId: '',
      rate: '',
      appliedAcreage: '',
      areaAppliedPercent: '100',
      notes: '',
      fieldId,
      newEvent: true,
      createdAt: new Date(),
    },
    FieldData: {
      id: uuidv4(),
      year: '',
      dominantSpecies: [],
      speciesModelInputId: '',
      paddockNum: 1,
      averagePaddockSize: additionalInfo?.fieldArea?.toFixed(2),
      notes: '',
      fieldId,
      newEvent: true,
      createdAt: new Date(),
    },
    AnimalData: {
      id: uuidv4(),
      year: '',
      herdName: '',
      operationTypeId: '',
      animalTypeId: '',
      averageWeight: '',
      headCount: '',
      enclosureSize: '',
      averageDaysGrazed: '',
      totalDaysGrazed: '',
      notes: '',
      newEvent: true,
      createdAt: new Date(),
    },
  };

  return defaultEvents[eventType];
}

export function getDateText(dateStr: string | Date, showHours = false, UTCTimezone = true): string {
  if (!dateStr) return '';
  const date: Date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;

  const dateProps: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(showHours && {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }),
    ...(UTCTimezone && {
      timeZone: 'UTC',
    }),
  };

  return showHours ? date?.toLocaleString('en-US', dateProps).replace(',', ' -') : date?.toLocaleDateString('en-US', dateProps);
}

export function getUnitName(unit: string) {
  return unit.includes('/Acre') ? unit.replace('/Acre', ' per acre') : unit.concat(' per acre');
}

export function eventsModified(eventUpdated, eventFull, typeAttributes: string[]) {
  return typeAttributes.some((attribute) => {
    if (eventUpdated[attribute] && eventFull[attribute] && Array.isArray(eventUpdated[attribute])) {
      return eventUpdated[attribute].join() !== eventFull[attribute]?.join();
    }
    return eventUpdated[attribute]?.toString()?.split('T')?.[0] !== eventFull[attribute]?.toString()?.split('T')?.[0];
  });
}

export function calculateInitialAppliedAcreage(fieldEvents: any[], fieldAcreage: number) {
  const usedAcreage = fieldEvents.reduce((acc, ev) => acc + Number(Number(ev.appliedAcreage).toFixed(2)), 0);
  const finalValue = (fieldAcreage - usedAcreage).toFixed(2);
  return Number(finalValue) > 0 ? finalValue : 0;
}

export const ErroredEventMapping: {
  [key in EventTypes]: (event: IEvents, otherEvent?: IDCPlantingEvent | IDCHarvestEvent[]) => boolean;
} = {
  Tillage: (event) => {
    const typedEvent = event as IDCTillageEvent;
    return !!typedEvent.missingData || !!typedEvent.duplicatedDate;
  },
  CoverCrop: (event) => {
    const typedEvent = event as IDCCoverCroppingEvent;
    return !!typedEvent.missingData || !!typedEvent.duplicatedDate || !!typedEvent.dateConflict || !!typedEvent.phEventOverlap;
  },
  Planting: (event) => {
    const typedEvent = event as IDCPlantingEvent;
    const issuesWithPlanting =
      !!typedEvent.missingData ||
      !!typedEvent.duplicatedDate ||
      !!typedEvent.phEventOverlap ||
      !!typedEvent.ccEventOverlap ||
      !!typedEvent.missingHarvestEvent;

    return issuesWithPlanting;
  },
  Harvest: (event) => {
    // if (!plantingEvent) throw Error('Harvest evaluation needs to receive planting event');
    const typedEvent = event as IDCHarvestEvent;
    return !!typedEvent.missingData || !!typedEvent.duplicatedDate;
  },
  Fertilization: (event) => {
    const typedEvent = event as IDCFertilizerEvent;
    return !!typedEvent.missingData || !!typedEvent.duplicatedDate;
  },
  Forage: (event) => {
    const typedEvent = event as IDCForageEvent;
    return !!typedEvent.missingData || !!typedEvent.duplicatedDate;
  },
  FieldData: () => {
    return false;
  },
  AnimalData: (event) => {
    const typedEvent = event as IDCAnimalDataEvent;
    return !!typedEvent.missingData;
  },
};

const findDateField = (event: IEvents) => {
  const dateFields = ['tillageDate', 'plantingDate', 'harvestDate', 'dateApplied', 'dateOfApplication'] as const;
  return dateFields.find((field) => field in event);
};

export const getEventTypeDateLabel = (event: IEvents) => findDateField(event) ?? '';

export const getEventTypeDate = (event: IEvents) => {
  const field = findDateField(event);
  if (field) return event[field];
  if (!('year' in event)) return null;

  return `${event.year}${'fieldId' in event ? '-01-01' : ''}`;
};

export const countErroredEventsByYear = (
  eventTypes: DCEventTypes,
  fields: Field[],
  practiceType: 'ROW_CROP' | 'PASTURE',
): Record<number | 'NO_DATE', number> => {
  const erroredByYear: Record<number | 'NO_DATE', number> = { NO_DATE: 0 };
  const cachedFields = new Map(fields.map((f) => [f.id, f.practiceType]));

  for (const eventType of Object.values(eventTypes)) {
    if (eventType.name === 'AnimalData' && practiceType === 'ROW_CROP') continue;

    for (const event of Object.values(eventType.events)) {
      if (!event.erroredEvent) continue;
      if (practiceType && event.fieldId && cachedFields.get(event.fieldId) !== practiceType) continue;

      const eventDate = getEventTypeDate(event);
      if (!eventDate) {
        erroredByYear['NO_DATE']++;
        continue;
      }

      const year = new Date(String(eventDate)).getUTCFullYear();
      erroredByYear[year] = (erroredByYear[year] ?? 0) + 1;
    }
  }

  return erroredByYear;
};

export const ORGANIC_SOURCE_TOOLTIP = (
  <Box direction={'column'} justify={'center'} margin={'0 0 0.25rem 21.75rem'}>
    <Box
      round={'0.25rem'}
      pad={'0.75rem 0.5rem'}
      height={'fit-content'}
      width={'25rem'}
      gap={'0.75rem'}
      justify={'center'}
      background={{ color: '#494949' }}>
      <Text>
        <b>On-site: </b>Manure originates from the farm/ranch.
      </Text>
      <Text>
        <b>Off-site controlled: </b>Imported manure from a managed source (e.g., stored aerobically/anaerobically with methane recovery for energy
        generation).
      </Text>
      <Text>
        <b>Off-site uncontrolled: </b>Imported manure from a unmanaged source (e.g. uncontrolled anaerobic lagoon, pond, tank or pit from which there
        is no recovery of methane for generation of heat and/or electricity).
      </Text>
      <Text>
        <b>Off-site unknown: </b>Origin of manure is unspecified.
      </Text>
    </Box>
    <Box
      margin={'0 0 0 1rem'}
      style={{
        width: 0,
        height: 0,
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
        borderTop: '10px solid #494949',
      }}
    />
  </Box>
);

const datePermissions = new Map<string, PermissionDetails>([
  [practiceCodes.seeding, { type: practiceNames.RPBS, permission: 'READONLY' }],
  [practiceCodes.fertilization, { type: practiceNames.RPF, permission: 'READONLY' }],
  [practiceCodes.grazing, { type: practiceNames.RPIG, permission: 'EDIT' }],
  [practiceCodes.noTillage, { type: practiceNames.NT, permission: 'EDIT' }],
  [practiceCodes.reducedTillage, { type: practiceNames.IT, permission: 'EDIT' }],
  [practiceCodes.coverCropping, { type: practiceNames.CC, permission: 'READONLY' }],
]);

export const getAllCodesByPermission = (permission: Permissions): Set<string> => {
  const codes = Array.from(datePermissions.values())
    .filter((p) => p.permission === permission)
    .map((p) => practiceCodes[p.type]);

  return new Set<string>(codes);
};

export const checkDatePermission = (practice: string, target: DatePermission) => {
  const permissionByCode = datePermissions.get(practice)?.permission;
  if (permissionByCode) return permissionByCode === target;

  const permissionByType = Array.from(datePermissions.values()).find((p) => p.type === practice)?.permission;
  return permissionByType === target;
};

export const displaySection = (label: string, events: IEvents[] | undefined, filters: string[], showErrors: boolean) => {
  const isVisible = !showErrors || !!events?.length;
  const isFiltered = filters.includes(label) || !filters.length;
  return isVisible && isFiltered;
};

export const SectionLabels: Record<EventTypes, string> = {
  Tillage: 'Tillage',
  Planting: 'Planting and Harvest',
  Harvest: 'Harvest',
  CoverCrop: 'Cover Cropping',
  Fertilization: 'Fertilizer and Amendment',
  Forage: 'Forage Management',
  FieldData: 'General Field Data',
  AnimalData: 'Animal Data',
};

export const checkIfEventsAreFromSameProducer = (currentEventTypes, newEventTypes) => {
  let currentJourneyId: string | undefined;
  let newJourneyId: string | undefined;
  for (const type in currentEventTypes) {
    if (Object.keys(currentEventTypes[type].events).length !== 0 && !currentJourneyId) {
      currentJourneyId = currentEventTypes[type].events[Object.keys(currentEventTypes[type].events)[0]].journeyId;
    }
    if (newEventTypes[type] && Object.keys(newEventTypes[type]).length !== 0 && !newJourneyId) {
      newJourneyId = newEventTypes[type][Object.keys(newEventTypes[type])[0]].journeyId;
    }
    if (currentJourneyId && newJourneyId) {
      if (newJourneyId !== currentJourneyId) {
        return false;
      }
      break;
    }
  }

  if (!currentJourneyId || !newJourneyId || currentJourneyId === newJourneyId) {
    return true;
  }

  // We should never return false from this statement
  console.error('Returning false from wrong place when checking journeyId from events');
  return false;
};

export const checkIfEventsAreFromJourney = (currentEventTypes: any, journeyId?: string) => {
  let currentJourneyId: string | undefined;
  for (const type in currentEventTypes) {
    if (Object.keys(currentEventTypes[type].events).length !== 0 && !currentJourneyId) {
      currentJourneyId = currentEventTypes[type].events[Object.keys(currentEventTypes[type].events)[0]].journeyId;
      break;
    }
  }

  return !currentJourneyId || !journeyId || currentJourneyId === journeyId;
};
